import { Field, useComponentProps, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  SiteMasterRouteFields,
  SitecoreReferenceItem,
  SiteRecruitingStudiesComponentData,
} from './types';

export const useSiteMasterRouteFields = (): SiteMasterRouteFields => {
  const { sitecoreContext } = useSitecoreContext();
  return (sitecoreContext?.route?.fields ?? {}) as SiteMasterRouteFields;
};

export const getFieldValue = (field?: { value?: string | number | boolean | null }): string => {
  if (field?.value === undefined || field?.value === null) {
    return '';
  }
  return String(field.value);
};

/** True when RTE has visible text or media. Empty tags, &nbsp;, and <br> do not count. */
export const hasVisibleRichText = (html?: string): boolean => {
  if (!html?.trim()) {
    return false;
  }

  if (/<img\b/i.test(html)) {
    return true;
  }

  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return Boolean(text);
};

export const extractNumberFromString = (raw?: string): number | null => {
  if (!raw?.trim()) {
    return null;
  }

  const match = raw.match(/\d+/);
  if (!match) {
    return null;
  }

  const value = Number(match[0]);
  return Number.isInteger(value) ? value : null;
};

export const getReferenceDisplayName = (
  item?: SitecoreReferenceItem | { value?: string }
): string => {
  if (!item) {
    return '';
  }

  if ('value' in item && typeof item.value === 'string') {
    return item.value;
  }

  const reference = item as SitecoreReferenceItem;
  return (
    getFieldValue(reference.fields?.title) ||
    getFieldValue(reference.fields?.name) ||
    getFieldValue(reference.fields?.label) ||
    ''
  );
};

export const getMultilistDisplayNames = (items?: SitecoreReferenceItem[]): string[] => {
  if (!items?.length) {
    return [];
  }

  return items.map((item) => getReferenceDisplayName(item)).filter(Boolean);
};

export const getMultilistDescriptions = (items?: SitecoreReferenceItem[]): string[] => {
  if (!items?.length) {
    return [];
  }

  return items.map((item) => getFieldValue(item.fields?.description)).filter(Boolean);
};

/** Display-name field of a referenced item, so Experience Editor can edit it in place. */
export const getReferenceDisplayField = (
  item?: SitecoreReferenceItem
): Field<string> | undefined => {
  const fields = item?.fields;
  return [fields?.title, fields?.name, fields?.label].find((field) => getFieldValue(field));
};

export const getMultilistDisplayFields = (items?: SitecoreReferenceItem[]): Field<string>[] => {
  if (!items?.length) {
    return [];
  }

  return items
    .map((item) => getReferenceDisplayField(item))
    .filter((field): field is Field<string> => Boolean(field));
};

export const getMultilistDescriptionFields = (items?: SitecoreReferenceItem[]): Field<string>[] => {
  if (!items?.length) {
    return [];
  }

  return items
    .map((item) => item.fields?.description)
    .filter((field): field is Field<string> => Boolean(getFieldValue(field)));
};

export const formatSiteAddress = (fields: SiteMasterRouteFields): string => {
  const parts = [
    getFieldValue(fields.siteAddress),
    getFieldValue(fields.siteCity),
    getReferenceDisplayName(fields.siteState),
    getFieldValue(fields.siteZip),
  ].filter(Boolean);

  return parts.join(', ');
};

export type OperatingHourEntry = {
  day: string;
  hours: string;
};

export const parseOperatingHours = (value?: string): OperatingHourEntry[] => {
  if (!value) {
    return [];
  }

  return value.split('&').map((entry) => {
    const [day, hours = ''] = entry.split('=');
    return {
      day: decodeURIComponent(day ?? ''),
      hours: decodeURIComponent(hours.replace(/%3A/g, ':')),
    };
  });
};

export const buildSiteDetailUrl = (siteName: string): string =>
  `/sites/${encodeURIComponent(siteName)}`;

export const getDirectionsUrl = (fields: SiteMasterRouteFields, mapLinkBase?: string): string => {
  const lat = getFieldValue(fields.siteLatitude);
  const lng = getFieldValue(fields.siteLongitude);
  const address = formatSiteAddress(fields);

  if (mapLinkBase) {
    if (lat && lng) {
      return `${mapLinkBase}${lat},${lng}`;
    }
    return address ? `${mapLinkBase}${encodeURIComponent(address)}` : '';
  }
  return '';
};

export const haversineDistanceMiles = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const truncateText = (text: string, maxLines: number, charsPerLine = 80): string => {
  const maxChars = maxLines * charsPerLine;
  if (text.length <= maxChars) {
    return text;
  }
  return `${text.slice(0, maxChars).trim()}...`;
};

/** Must match Sitecore rendering componentName / componentBuilder key. */
export const SITE_RECRUITING_STUDIES_COMPONENT_NAME = 'SiteRecruitingStudies';

type RenderingNode = {
  uid?: string;
  componentName?: string;
  placeholders?: Record<string, RenderingNode[]>;
};

/**
 * Walks route placeholders (including nested) to find a rendering UID by componentName.
 */
export const findRenderingUidByComponentName = (
  route: { placeholders?: Record<string, RenderingNode[]> } | null | undefined,
  componentName: string
): string | undefined => {
  if (!route?.placeholders) {
    return undefined;
  }

  const walk = (items?: RenderingNode[]): string | undefined => {
    if (!items?.length) {
      return undefined;
    }

    for (const item of items) {
      if (item.componentName === componentName && item.uid) {
        return item.uid;
      }

      if (item.placeholders) {
        for (const nested of Object.values(item.placeholders)) {
          const found = walk(nested);
          if (found) {
            return found;
          }
        }
      }
    }

    return undefined;
  };

  for (const items of Object.values(route.placeholders)) {
    const found = walk(items);
    if (found) {
      return found;
    }
  }

  return undefined;
};

/**
 * Reads SiteRecruitingStudies componentProps (single GraphQL owner).
 * Hero / Explore use this instead of their own getStaticProps.
 */
export const useSiteRecruitingStudiesProps = (): SiteRecruitingStudiesComponentData => {
  const { sitecoreContext } = useSitecoreContext();
  const ownerUid =
    findRenderingUidByComponentName(
      sitecoreContext?.route as { placeholders?: Record<string, RenderingNode[]> } | undefined,
      SITE_RECRUITING_STUDIES_COMPONENT_NAME
    ) ?? '';

  return useComponentProps<SiteRecruitingStudiesComponentData>(ownerUid) ?? {};
};
